import { Invitation, UserRole, UserWithRole } from '@fsp/types';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ArrowUpward } from '@mui/icons-material';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import {
  canManageInvitations,
  fetchInvitations,
  getAvailableRoles,
  sendInvitation,
} from '../../services/invitation';
import { fetchTeamMembers } from '../../services/team';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import ContentWrapper from '../../layout/ContentWrapper';
import TeamMemberRow from './components/TeamMemberRow';
import TeamPendingInvitationRow from './components/TeamPendingInvitationRow';
import TeamSkeleton from './components/TeamSkeleton';
import QuotaDisplay from '../../components/QuotaDisplay';
import { getTeamMemberQuotaInfo } from '../../utils/quota';
import { useNavigate } from 'react-router-dom';

export const TeamRoles = [UserRole.fsp, UserRole.OWNER, UserRole.ADMIN];

interface InviteFormData {
  email: string;
  role: UserRole;
}

const Team: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentOrganisation, organisationId, user } = useAuth();
  const navigate = useNavigate();

  // State
  const [teamMembers, setTeamMembers] = useState<UserWithRole[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [invitationsLoading, setInvitationsLoading] = useState<boolean>(false);

  // Invite dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteFormData>({
    email: '',
    role: UserRole.MEMBER,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  if (!organisationId) {
    setLoading(true);
  }

  // API functions
  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const members = await fetchTeamMembers(organisationId!);
      setTeamMembers(members);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to fetch team members', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = useCallback(async () => {
    if (!currentOrganisation) return;
    if (!canManageInvitations(currentOrganisation?.role as UserRole)) return;

    try {
      setInvitationsLoading(true);
      const invitationList = await fetchInvitations(organisationId!);
      setInvitations(invitationList);
    } catch (err: any) {
      enqueueSnackbar(err.message, { variant: 'error' });
      setInvitations([]);
    } finally {
      setInvitationsLoading(false);
    }
  }, [currentOrganisation, enqueueSnackbar, organisationId]);

  // Callback handlers for child components
  const handleMemberUpdated = (updatedMember: UserWithRole) => {
    setTeamMembers((prev) =>
      prev.map((member) => (member.id === updatedMember.id ? updatedMember : member))
    );
  };

  const handleMemberDeleted = (memberId: string) => {
    setTeamMembers((prev) => prev.filter((member) => member.id !== memberId));
  };

  const handleInvitationUpdated = (updatedInvitation: Invitation) => {
    setInvitations((prev) =>
      prev.map((inv) => (inv.id === updatedInvitation.id ? updatedInvitation : inv))
    );
  };

  const handleInvitationDeleted = (invitationId: string) => {
    setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
  };

  const handleOpenInviteDialog = () => {
    setInviteDialogOpen(true);
    setDialogError(null);
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteForm({ email: '', role: UserRole.MEMBER });
    setDialogError(null);
  };

  const handleSendInvitation = async () => {
    try {
      setIsSubmitting(true);
      setDialogError(null);

      await sendInvitation(inviteForm.email, inviteForm.role, organisationId!);

      enqueueSnackbar(`Invitation sent successfully to ${inviteForm.email}`, {
        variant: 'success',
      });
      handleCloseInviteDialog();
      await loadInvitations();
    } catch (err: any) {
      setDialogError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setInviteForm((prev) => ({ ...prev, role: event.target.value as UserRole }));
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInviteForm((prev) => ({ ...prev, email: event.target.value }));
  };

  const handleRefresh = () => {
    loadTeamMembers();
    if (canManageInvitations(currentOrganisation?.role as UserRole)) {
      loadInvitations();
    }
  };

  const handleClose = () => {
    navigate('/settings/');
  };

  const headerActions = (
    <IconButton onClick={handleClose}>
      <ArrowUpward />
    </IconButton>
  );

  useEffect(() => {
    loadTeamMembers();
  }, []);

  useEffect(() => {
    if (currentOrganisation?.role && canManageInvitations(currentOrganisation?.role as UserRole)) {
      loadInvitations();
    }
  }, [currentOrganisation?.role, loadInvitations]);
  const canManageTeam =
    (currentOrganisation?.role as UserRole) === UserRole.OWNER ||
    (currentOrganisation?.role as UserRole) === UserRole.ADMIN;

  const teamMemberQuota = getTeamMemberQuotaInfo(
    currentOrganisation,
    teamMembers.length + invitations.length
  );

  const isFormValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return inviteForm.email.trim() !== '' && emailRegex.test(inviteForm.email);
  };

  if (loading || !user) {
    return (
      <ContentWrapper breadcrumb>
        <PaperSection title="Team Management" variant="default" action={headerActions}>
          <TeamSkeleton />
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper breadcrumb>
      <PaperSection
        title="Team Management"
        variant="default"
        action={
          <Stack direction="row" spacing={1}>
            {canManageTeam && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenInviteDialog}
                  disabled={!teamMemberQuota.canAdd}
                >
                  Invite Team Member
                </Button>
                <IconButton onClick={handleRefresh} disabled={loading || invitationsLoading}>
                  <RefreshIcon />
                </IconButton>
              </>
            )}
            {headerActions}
          </Stack>
        }
      >
        <QuotaDisplay label="Team members" quotaInfo={teamMemberQuota} />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Last Active</TableCell>
                {canManageTeam && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Divider row - only show if there are both invitations and members */}
              {invitations.length > 0 && teamMembers.length > 0 && (
                <TableRow>
                  <TableCell colSpan={canManageTeam ? 6 : 5} sx={{ py: 1, px: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Pending invitations
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* Render invitations first */}
              {invitations.map((invitation) => (
                <TeamPendingInvitationRow
                  key={`invitation-${invitation.id}`}
                  invitation={invitation}
                  organisationId={organisationId!}
                  currentUserRole={currentOrganisation?.role as UserRole}
                  onInvitationUpdated={handleInvitationUpdated}
                  onInvitationDeleted={handleInvitationDeleted}
                />
              ))}

              {/* Divider row - only show if there are both invitations and members */}
              {invitations.length > 0 && teamMembers.length > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={canManageTeam ? 6 : 5}
                    sx={{
                      borderTop: '1px solid',
                      borderTopColor: 'divider',
                      backgroundColor: 'grey.50',
                      py: 1,
                      px: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Team Members
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* Render actual members */}
              {teamMembers.map((member) => (
                <TeamMemberRow
                  key={`member-${member.id}`}
                  member={member}
                  currentUserId={user?.id}
                  organisationId={organisationId!}
                  currentUserRole={currentOrganisation?.role as UserRole}
                  onMemberUpdated={handleMemberUpdated}
                  onMemberDeleted={handleMemberDeleted}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Send Invitation Dialog */}
        <Dialog open={inviteDialogOpen} onClose={handleCloseInviteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Send Team Invitation</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Send an invitation to join your organisation. The invitation will expire in 7 days.
            </DialogContentText>

            {dialogError && (
              <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                {dialogError}
              </Alert>
            )}

            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={inviteForm.email}
                onChange={handleEmailChange}
                helperText="An invitation email will be sent to this address"
                disabled={isSubmitting}
                error={inviteForm.email !== '' && !isFormValid()}
              />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={inviteForm.role}
                  onChange={handleRoleChange}
                  label="Role"
                  disabled={isSubmitting}
                >
                  {getAvailableRoles(currentOrganisation?.role as UserRole).map((role) => (
                    <MenuItem key={role} value={role}>
                      {role === UserRole.VIEWER && 'Viewer - Can view data only'}
                      {role === UserRole.MEMBER && 'Member - Can view and edit'}
                      {role === UserRole.APPROVER && 'Approver - Can approve food safety plans'}
                      {role === UserRole.AUDITOR && 'Auditor - Can audit food safety plans'}
                      {role === UserRole.ADMIN && 'Admin - Full access except billing'}
                      {role === UserRole.OWNER && 'Owner - Full access including billing'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseInviteDialog} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSendInvitation}
              variant="contained"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogActions>
        </Dialog>
      </PaperSection>
    </ContentWrapper>
  );
};

export default Team;
